"use client";

import React, { createContext, useContext, ReactNode } from "react";
import { useCardData, CardData } from "@/hooks/useCardData";

interface CardDataContextValue {
  cardData: CardData;
  aaData: CardData;
  isLoading: boolean;
  error: Error | null;
}

const CardDataContext = createContext<CardDataContextValue | undefined>(
  undefined,
);

interface CardDataProviderProps {
  children: ReactNode;
  game?: string;
  initialData?: CardData;
  initialAAData?: CardData;
}

/**
 * Provider that fetches and caches card data for the entire component tree
 * This ensures we only fetch card data once per page load
 */
export function CardDataProvider({
  children,
  game = "one-piece",
  initialData,
  initialAAData,
}: CardDataProviderProps) {
  console.log(
    "[TEST] CardDataProvider - initialAAData provided:",
    !!initialAAData,
  );
  if (initialData) {
    console.log(
      "[TEST] CardDataProvider - initialData has",
      Object.keys(initialData).length,
      "cards",
    );
    console.log(
      "[TEST] CardDataProvider - EB05 cards in initialData:",
      Object.keys(initialData).filter((id) => id.startsWith("EB05")).length,
    );
  }
  if (initialAAData) {
    console.log(
      "[TEST] CardDataProvider - initialAAData has",
      Object.keys(initialAAData).length,
      "entries",
    );
  }

  const { cardData, isLoading, error } = useCardData({
    game,
    enabled: !initialData, // Don't fetch if we already have initial data
  });

  const value = {
    cardData: initialData || cardData,
    aaData: initialAAData || {},
    isLoading: initialData ? false : isLoading,
    error: initialData ? null : error,
  };

  console.log(
    "[TEST] CardDataProvider - final cardData has",
    Object.keys(value.cardData).length,
    "cards",
  );
  console.log(
    "[TEST] CardDataProvider - final aaData has",
    Object.keys(value.aaData).length,
    "entries",
  );

  return (
    <CardDataContext.Provider value={value}>
      {children}
    </CardDataContext.Provider>
  );
}

/**
 * Hook to access card data from anywhere in the component tree
 * Must be used within a CardDataProvider
 */
export function useCardDataContext(): CardDataContextValue {
  const context = useContext(CardDataContext);
  if (context === undefined) {
    throw new Error(
      "useCardDataContext must be used within a CardDataProvider",
    );
  }
  return context;
}
